/**
 * 공통 QA 체크리스트 + 현장별 QA 진행 API 클라이언트.
 * 백엔드 /v0.1/site-management/qa-template/items, /projects/:id/qa-items, /qa-items/:id 와 1:1.
 *
 * 공통 항목은 현장 목록의 [QA 리스트] 에서 admin 이 관리하고, 현장은 [공통 QA 가져오기] 로
 * 복사해 진행한다. 복사본이라 공통 항목을 고쳐도 이미 진행한 현장 기록은 바뀌지 않는다.
 */
import { API_ENDPOINTS } from '../config/api';
import { apiClient } from './api/apiClient';
import {
  ApiItemResponse,
  ApiListResponse,
  ApiOkResponse,
  ensureOk,
  unwrapItem,
  unwrapList,
} from './api/response';

export type QaResult = 'pending' | 'pass' | 'fail';

export const QA_RESULT_LABEL: Record<QaResult, string> = {
  pending: '미진행',
  pass: '성공',
  fail: '실패',
};

export interface QaTemplateItem {
  id: number;
  category: string;
  title: string;
  method: string;
  /** 기본 담당자 — 현장으로 가져갈 때 복사된다. */
  assignee: string;
  sort_order: number;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface QaTemplateItemInput {
  category?: string;
  title?: string;
  method?: string;
  assignee?: string;
  sort_order?: number;
}

export interface SiteQaItem {
  id: number;
  site_project_id: number;
  template_item_id?: number | null;
  category: string;
  title: string;
  method: string;
  sort_order: number;
  /** QA 가능 여부 — false 면 결과는 항상 미진행. */
  applicable: boolean;
  /** QA 불가 사유 (불가일 때 필수) */
  unavailable_reason: string;
  result: QaResult;
  fail_reason: string;
  /** 성공 날짜 YYYY-MM-DD */
  passed_at?: string | null;
  assignee: string;
  note: string;
  updated_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface SiteQaItemUpdate {
  category?: string;
  title?: string;
  method?: string;
  sort_order?: number;
  applicable?: boolean;
  /** applicable=false 면 필수 */
  unavailable_reason?: string;
  result?: QaResult;
  fail_reason?: string;
  /** "" 이면 지운다. 성공인데 비우면 서버가 오늘 날짜를 넣는다. */
  passed_at?: string;
  assignee?: string;
  note?: string;
}

/** 가져오기 때 공통 항목 하나에 대한 선택. 불가면 사유 필수. */
export interface QaImportChoice {
  template_item_id: number;
  applicable: boolean;
  unavailable_reason?: string;
}

interface ImportResponse extends ApiListResponse<SiteQaItem> {
  added: number;
}

export class SiteQaService {
  static async listTemplate(): Promise<QaTemplateItem[]> {
    const resp = await apiClient.get<ApiListResponse<QaTemplateItem>>(API_ENDPOINTS.SITE_MANAGEMENT.QA.TEMPLATE_LIST);
    return unwrapList(resp, '공통 QA 목록 조회 실패');
  }

  static async createTemplate(data: QaTemplateItemInput): Promise<QaTemplateItem> {
    const resp = await apiClient.post<ApiItemResponse<QaTemplateItem>>(API_ENDPOINTS.SITE_MANAGEMENT.QA.TEMPLATE_CREATE, data);
    return unwrapItem(resp, '공통 QA 항목 추가 실패');
  }

  static async updateTemplate(itemId: number, data: QaTemplateItemInput): Promise<QaTemplateItem> {
    const resp = await apiClient.put<ApiItemResponse<QaTemplateItem>>(API_ENDPOINTS.SITE_MANAGEMENT.QA.TEMPLATE_UPDATE(itemId), data);
    return unwrapItem(resp, '공통 QA 항목 수정 실패');
  }

  static async deleteTemplate(itemId: number): Promise<void> {
    const resp = await apiClient.delete<ApiOkResponse>(API_ENDPOINTS.SITE_MANAGEMENT.QA.TEMPLATE_DELETE(itemId));
    ensureOk(resp, '공통 QA 항목 삭제 실패');
  }

  static async listSite(projectId: number): Promise<SiteQaItem[]> {
    const resp = await apiClient.get<ApiListResponse<SiteQaItem>>(API_ENDPOINTS.SITE_MANAGEMENT.QA.SITE_LIST(projectId));
    return unwrapList(resp, '현장 QA 목록 조회 실패');
  }

  /**
   * 공통 항목 중 이 현장에 아직 없는 것만 가져온다. items 로 항목별 QA 가능 여부·불가 사유를
   * 넘기고, 넘기지 않은 항목은 가능으로 들어간다. added = 새로 들어간 수.
   */
  static async importTemplate(
    projectId: number,
    choices: QaImportChoice[] = []
  ): Promise<{ added: number; items: SiteQaItem[]; message: string }> {
    const resp = await apiClient.post<ImportResponse>(API_ENDPOINTS.SITE_MANAGEMENT.QA.SITE_IMPORT(projectId), {
      items: choices,
    });
    const items = unwrapList(resp, '공통 QA 가져오기 실패');
    return { added: resp.data.added ?? 0, items, message: resp.data.message };
  }

  static async createSite(projectId: number, data: QaTemplateItemInput): Promise<SiteQaItem> {
    const resp = await apiClient.post<ApiItemResponse<SiteQaItem>>(API_ENDPOINTS.SITE_MANAGEMENT.QA.SITE_CREATE(projectId), data);
    return unwrapItem(resp, '현장 QA 항목 추가 실패');
  }

  static async updateSite(itemId: number, data: SiteQaItemUpdate): Promise<SiteQaItem> {
    const resp = await apiClient.put<ApiItemResponse<SiteQaItem>>(API_ENDPOINTS.SITE_MANAGEMENT.QA.SITE_UPDATE(itemId), data);
    return unwrapItem(resp, 'QA 결과 저장 실패');
  }

  static async deleteSite(itemId: number): Promise<void> {
    const resp = await apiClient.delete<ApiOkResponse>(API_ENDPOINTS.SITE_MANAGEMENT.QA.SITE_DELETE(itemId));
    ensureOk(resp, '현장 QA 항목 삭제 실패');
  }
}
